<template>
  <div>
    <CallDialog
      v-if="$store.getters['calls/incomingCallActive']"
      :call-details="$store.getters['calls/incomingCallDetails']"
      :is-incoming="true"
    />

    <CallDialog
      v-if="$store.getters['calls/outgoingCallActive']"
      :call-details="$store.getters['calls/outgoingCallDetails']"
      :is-incoming="false"
    />

    <CallDetailsDrawer
      source="root"
      :id="SSID"
      v-model:drawer-visible="showCallDetails"
    />

    <DialerTermsAndConditions
      v-model:visible="showConsentForm"
      source="call_wrapper"
    />

    <Dialpad v-if="showDialpad" source="calls_wrapper" />

    <CallAddCreditModal
      source="call_wrapper"
      v-model:visible="showAddCreditModal"
    />

    <AddCallerIDModal
      v-model:visible="showAddCallerIDModal"
      :provider="callerIDProvider"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { callCampaignStatuses } from "@/src/constants/callConstants.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  initializeDevice,
  toUpdateCallCampaign,
  redirectToLeadsOrContacts,
} from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import CallDialog from "@/src/views/calls/call_widget/CallDialog.vue";
import CallDetailsDrawer from "@/src/views/calls/CallDetailsDrawer.vue";
import Dialpad from "@/src/components/telephony_components/dialer/Dialpad.vue";
import CallAddCreditModal from "@/src/components/telephony_components/billing/CallAddCreditModal.vue";
import AddCallerIDModal from "@/src/components/telephony_components/phone_number/caller_id/AddCallerIDModal.vue";
import DialerTermsAndConditions from "@/src/components/telephony_components/dialer/DialerTermsAndConditions.vue";

const showCallDetails = ref(false);
const showConsentForm = ref(false);
const SSID = ref(null);
const showAddCallerIDModal = ref(false);
const callerIDProvider = ref(telephonyProviders.twilio);

const showDialpad = computed(() => store.getters["calls/showDial"]);
const showAddCreditModal = computed({
  get: () => store.getters["calls/showAddCreditModal"],
  set: (val) => store.dispatch("calls/setShowAddCreditModal", val),
});

const handlePauseCampaign = async () => {
  try {
    const campaignId = store.getters["callsCampaign/currentCampaignId"];
    if (campaignId) {
      const payload = { status: callCampaignStatuses.PAUSED };
      await toUpdateCallCampaign(campaignId, payload);
    }
  } catch (error) {
    console.error("An error occurred while handling pause campaign: ", error);
  }
};

const handleBeforeUnload = async (event) => {
  try {
    if (store.getters["callsCampaign/isCurrentCampaign"]) {
      event.preventDefault();
      event.returnValue = "";
      await handlePauseCampaign();
    }
  } catch (error) {
    console.error("An error occurred while handling before unload: ", error);
  }
};

const handleCallEnded = (id) => {
  try {
    SSID.value = id;
    setTimeout(() => {
      showCallDetails.value = true;
      store.dispatch("calls/setDispositionDrawerOpen", true);
    }, 600);
  } catch (error) {
    console.error("An error occurred while handling call ended: ", error);
  }
};

watch(showCallDetails, async (isOpen, wasOpen) => {
  if (wasOpen && !isOpen) {
    await store.dispatch("calls/setDispositionDrawerOpen", false);
    const pending = store.getters["calls/pendingCampaignRedirect"];
    if (pending) {
      await store.dispatch("calls/setPendingCampaignRedirect", null);
      await redirectToLeadsOrContacts(pending);
    }
  }
});

const handleOpenConsentForm = (data) => {
  showConsentForm.value = data;
};

const handleOpenAddCallerIDModal = (data) => {
  try {
    if (data?.provider) {
      callerIDProvider.value = data.provider;
    }

    showAddCallerIDModal.value = true;
  } catch (error) {
    console.error(
      "An error occurred while handling open add caller ID modal: ",
      error,
    );
  }
};

const initializeTelephonySDKs = async () => {
  const config = {};

  try {
    const twilioResponse = await CallService.getCallToken({
      provider: telephonyProviders.twilio,
    });
    if (twilioResponse?.success) {
      config.twilioToken = twilioResponse.token;
    }
  } catch (error) {
    console.error("Failed to get Twilio token:", error);
  }

  try {
    const plivoResponse = await CallService.getCallToken({
      provider: telephonyProviders.plivo,
    });
    if (plivoResponse?.success) {
      config.plivoCredentials = {
        username: plivoResponse.token.username,
        password: plivoResponse.token.password,
        token: plivoResponse.token.token,
      };
    }
  } catch (error) {
    console.error("Failed to get Plivo credentials:", error);
  }

  try {
    const plivoInternationalResponse = await CallService.getCallToken({
      provider: telephonyProviders.plivoInternational,
    });
    if (plivoInternationalResponse?.success) {
      config.plivoInternationalCredentials = {
        username: plivoInternationalResponse.token.username,
        password: plivoInternationalResponse.token.password,
        token: plivoInternationalResponse.token.token,
      };
    }
  } catch (error) {
    console.error("Failed to get Plivo International credentials:", error);
  }

  if (
    config.twilioToken ||
    config.plivoCredentials ||
    config.plivoInternationalCredentials
  ) {
    try {
      await initializeDevice(config);
    } catch (error) {
      console.error("Failed to initialize telephony devices:", error);
    }
  }
};

const toCheckRecordingConsent = async () => {
  try {
    const response = await CallService.checkRecordingConsent();
    if (response?.success) {
      await store.dispatch(
        "calls/setRecordingConsentStatus",
        response?.consent,
      );
    }
  } catch (error) {
    console.error(
      "An error occurred while checking recording consent: ",
      error,
    );
  }
};

onMounted(async () => {
  await toCheckRecordingConsent();
  await initializeTelephonySDKs();
  eventBus.on("call-ended", handleCallEnded);
  eventBus.on("open-consent-form", handleOpenConsentForm);
  eventBus.on("open-add-caller-id-modal", handleOpenAddCallerIDModal);

  eventBus.on("initialise-sdk", initializeTelephonySDKs);

  window.addEventListener("beforeunload", handleBeforeUnload);

  if (store.getters["callsCampaign/isCurrentCampaign"]) {
    await handlePauseCampaign();
  }
});

onUnmounted(() => {
  eventBus.off("call-ended", handleCallEnded);
  eventBus.off("open-consent-form", handleOpenConsentForm);
  eventBus.off("open-add-caller-id-modal", handleOpenAddCallerIDModal);

  eventBus.off("initialise-sdk", initializeTelephonySDKs);

  window.removeEventListener("beforeunload", handleBeforeUnload);
});
</script>
